/**
 * AppConfig — central place for all environment-aware URL resolution.
 *
 * Three known server environments (frontend + backend on the same host via wildcard SSL):
 *   Production  → liriworks.com         (and *.liriworks.com for tenants)
 *   Testing     → test.liriworks.com    (and *.test.liriworks.com for tenants)
 *   Development → dev-liriworks.lirisoft.net (and *.dev-liriworks.lirisoft.net for tenants)
 *
 * Tenant subdomains follow the pattern: {tenant}.{rootDomain}
 *   e.g., acme.liriworks.com → tenant="acme", API at https://acme.liriworks.com/
 *
 * Localhost always falls back to the development server.
 * REACT_APP_BASE_URL env var overrides everything.
 */

// NOTE: More specific (longer) domains must come before more general ones.
// e.g., "test.liriworks.com" must precede "liriworks.com" so that
// "company.test.liriworks.com" doesn't get misidentified as a "liriworks.com" tenant.
const ENVIRONMENTS = [
  {
    name: "testing" as const,
    rootDomain: "test.liriworks.com",
    apiUrl: "https://test.liriworks.com/",
    wsUrl: "wss://test.liriworks.com/portal",
    registrationApiUrl: "https://api-registration.lirisoft.net/api/",
    frontendUrl: "https://test.liriworks.com",
  },
  {
    name: "development" as const,
    rootDomain: "dev-liriworks.lirisoft.net",
    apiUrl: "https://dev-liriworks.lirisoft.net/",
    wsUrl: "wss://dev-liriworks.lirisoft.net/portal",
    registrationApiUrl: "https://api-registration.lirisoft.net/api/",
    frontendUrl: "https://dev-liriworks.lirisoft.net",
  },
  {
    name: "production" as const,
    rootDomain: "liriworks.com",
    apiUrl: "https://liriworks.com/",
    wsUrl: "wss://liriworks.com/portal",
    registrationApiUrl: "https://api-registration.liriworks.com/api/",
    frontendUrl: "https://liriworks.com",
  },
] as const;

// Default backend when running on localhost
const LOCAL_DEV_API_URL = "https://dev-liriworks.lirisoft.net/";
const LOCAL_DEV_WS_URL = "wss://dev-liriworks.lirisoft.net/portal";

class AppConfig {
  constructor() {
    if (typeof window !== "undefined") {
      console.log(
        "[AppConfig] hostname:", window.location.hostname,
        "| env:", this.detectEnvironmentName(),
        "| tenant:", this.getTenant() ?? "(none)"
      );
    }
  }

  private isLocalHost(): boolean {
    if (typeof window === "undefined") return false;
    const { hostname } = window.location;
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
  }


  private detectEnvironment() {
    if (typeof window === "undefined" || this.isLocalHost()) return null;
    const { hostname } = window.location;
    for (const env of ENVIRONMENTS) {
      if (hostname === env.rootDomain || hostname.endsWith(`.${env.rootDomain}`)) {
        return env;
      }
    }
    return null;
  }

  private detectEnvironmentName(): string {
    if (this.isLocalHost()) return "local";
    return this.detectEnvironment()?.name ?? "unknown";
  }


  getTenant(): string | null {
    if (this.isLocalHost()) {
      // Centralized fallback for local development so we don't return null
      return "testhrm";
    }

    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length > 1) { // Changed to > 1 to ensure it actually has a subdomain
        return parts[0];
      }
    }
    return null;
  }

  /**
   * Returns the backend API base URL for the current server.
   *
   * Priority:
   *   1. REACT_APP_BASE_URL env var (explicit override)
   *   2. Localhost → development server
   *   3. Known environment or its tenant subdomain → same protocol + hostname
   *      (backend wildcard SSL handles *.rootDomain traffic)
   *   4. Unknown hostname → dynamic same-origin fallback
   */
  getBaseUrl(): string {
    // 1. Explicit env override
    const envBaseUrl = process.env.REACT_APP_BASE_URL;
    if (envBaseUrl) return envBaseUrl;

    // 2. Localhost → dev server
    if (this.isLocalHost()) return LOCAL_DEV_API_URL;

    if (typeof window !== "undefined") {
      const { protocol, hostname } = window.location;

      // 3. For any recognized server (or its tenant subdomains), use the current
      //    hostname as the API base. The backend's wildcard SSL cert + reverse proxy
      //    routes *.rootDomain traffic to the correct server.
      //    e.g.:
      //      liriworks.com              → https://liriworks.com/
      //      acme.liriworks.com         → https://acme.liriworks.com/
      //      test.liriworks.com         → https://test.liriworks.com/
      //      acme.test.liriworks.com    → https://acme.test.liriworks.com/
      if (this.detectEnvironment()) {
        return `${protocol}//${hostname}/`;
      }

      // 4. Unknown hostname (e.g., custom domain) → same-origin dynamic fallback
      return `${protocol}//${hostname}/`;
    }

    // SSR / Node fallback
    return ENVIRONMENTS[2].apiUrl; // production
  }

  /**
   * Returns the WebSocket base URL for the current server.
   *
   * Converts http→ws / https→wss from the base URL, then appends the
   * portal path suffix that the backend WebSocket endpoint expects.
   */
  getWebSocketUrl(): string {
    // 1. Localhost → dev WebSocket
    if (this.isLocalHost()) return LOCAL_DEV_WS_URL;

    if (typeof window !== "undefined") {
      const { protocol, hostname } = window.location;
      // Convert http(s) to ws(s)
      const wsProtocol = protocol === "https:" ? "wss:" : "ws:";
      return `${wsProtocol}//${hostname}/portal`;
    }

    return LOCAL_DEV_WS_URL;
  }

  // ---------------------------------------------------------------------------
  // Registration & Payment Helpers
  // ---------------------------------------------------------------------------

  getRegistrationApiUrl(): string {
    const env = this.detectEnvironment();
    if (env) {
      return env.registrationApiUrl;
    }
    // Fallback if environment is unknown
    return "https://api-registration.lirisoft.net/api/";
  }

  getFrontendUrl(): string {
    // Force testing domain for localhost callbacks
    if (this.isLocalHost()) {
      return "http://localhost:3001/";
    }
    const env = this.detectEnvironment();
    if (env) {
      return env.frontendUrl;
    }
    // Fallback if running on custom/unknown domain
    return typeof window !== "undefined" ? window.location.origin : "https://prodliri.com";
  }

  getPaymentRedirectUrl(registrationId: string | number): string {
    const regApiUrl = this.getRegistrationApiUrl();

    // Extract root domain from registration API
    const apiBaseClean = regApiUrl.endsWith("/api/")
      ? regApiUrl.slice(0, -5)
      : regApiUrl;

    const apiOrigin = new URL(apiBaseClean).origin;

    // Use our getFrontendUrl method for the callback domain
    const frontendBaseUrl = this.getFrontendUrl();
    const cleanFrontendBase = frontendBaseUrl.endsWith('/') ? frontendBaseUrl.slice(0, -1) : frontendBaseUrl;

    const callbackUrl = `${cleanFrontendBase}/payment-result`;

    return `${apiOrigin}/payment.html?registrationId=${registrationId}&callbackUrl=${encodeURIComponent(callbackUrl)}`;
  }

  // ---------------------------------------------------------------------------
  // Redirect / OAuth helpers
  // ---------------------------------------------------------------------------

  private ensureTrailingSlash(url: string): string {
    return url.endsWith("/") ? url : `${url}/`;
  }

  getLocalURL(): string {
    const configuredRedirectUrl = process.env.REACT_APP_REDIRECT_URL?.trim();
    if (configuredRedirectUrl) {
      return this.ensureTrailingSlash(configuredRedirectUrl);
    }

    if (typeof window !== "undefined" && window.location.origin) {
      const publicUrl = process.env.PUBLIC_URL || "";
      return this.ensureTrailingSlash(`${window.location.origin}${publicUrl}`);
    }

    return LOCAL_DEV_API_URL;
  }

  googleLogin(): string {
    // We pass the tenant as a query param so the backend knows which DB to use
    const tenant = this.getTenant() || "testhrm";
    const googleLoginUrl = `${this.getBaseUrl()}oauth2/authorization/google?redirect_uri=${encodeURIComponent(this.getLocalURL())}&tenant=${tenant}`;

    console.log("Google Login URL: ", googleLoginUrl);
    return googleLoginUrl;
  }

  microsoftLogin(): string {
    const url = `${this.getBaseUrl()}oauth2/authorization/microsoft?redirect_uri=${encodeURIComponent(this.getLocalURL())}`;
    console.log("[AppConfig] Microsoft login URL:", url);
    return url;
  }
}

const appConfig = new AppConfig();
export default appConfig;
